import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Newspaper, CalendarDays, Globe2, Info, Mail, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  { label: null, items: [{ to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", id: "dashboard" }] },
  { label: "Content", items: [
    { to: "/admin/news", icon: Newspaper, label: "News", id: "news" },
    { to: "/admin/events", icon: CalendarDays, label: "Events", id: "events" },
    { to: "/admin/federations", icon: Globe2, label: "Federations", id: "federations" },
  ]},
  { label: "Website", items: [
    { to: "/admin/about", icon: Info, label: "About", id: "about" },
    { to: "/admin/contact", icon: Mail, label: "Contact", id: "contact" },
  ]},
];

export function AdminLayout() {
  const { logout, admin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); toast.success("Signed out"); navigate("/admin/login"); };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-ukf-red text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
    }`;

  const Sidebar = (
    <div className="flex h-full flex-col">
      <Link to="/admin" className="flex items-center gap-3 border-b border-white/10 px-5 py-5">
        <img src="/branding/ukf-emblem.png" alt="UKF" className="h-10 w-auto" />
        <div className="leading-none">
          <p className="font-display text-base text-white">UKF</p>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Administration</p>
        </div>
      </Link>
      <nav className="flex-1 space-y-6 overflow-y-auto p-4">
        {GROUPS.map((g, gi) => (
          <div key={gi}>
            {g.label && <p className="mb-2 px-3 font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-500">{g.label}</p>}
            <div className="space-y-1">
              {g.items.map((it) => (
                <NavLink key={it.id} to={it.to} end={it.end} className={linkCls} data-testid={`admin-nav-${it.id}`} onClick={() => setOpen(false)}>
                  <it.icon className="h-4 w-4" /> {it.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <a href="/" target="_blank" rel="noopener noreferrer" className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-white" data-testid="admin-view-site">
          <ExternalLink className="h-4 w-4" /> View website
        </a>
        <button onClick={doLogout} data-testid="admin-logout-button" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-white">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
        <p className="mt-3 truncate px-3 text-xs text-slate-500">{admin?.email}</p>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-100">
      <aside className="fixed inset-y-0 left-0 hidden w-64 bg-ukf-ink lg:block">{Sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-ukf-ink">{Sidebar}</aside>
        </div>
      )}
      <div className="flex flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu" className="flex h-10 w-10 items-center justify-center rounded-md text-slate-700">
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-serif text-lg font-bold text-ukf-ink">UKF Admin</span>
          <span className="w-10" />
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
